import React from 'react';
import { rem } from 'modules/helpers/style';
import RatingStars from 'components/generic/ratingStars';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import type { ProductReviewListModel } from 'autogen/swagger/all';
import { useQuickView } from 'components/quickView';
import * as Typography from 'components/__deprecated/typography';
import { ColorNeutralWhite, Spacing8, Spacing16, Spacing24, ColorBrandBlue1 } from 'autogen/design-tokens/tokens';
import { useTranslation } from 'react-i18next';
import { BasedOnXReviews, RatingSpreadHeadline } from 'autogen/translation-keys/trans-website-productreview-list';
import { PercentRecommended } from 'autogen/translation-keys/trans-website-productreview-header';
import RatingSpread from './ratingSpread';
import WriteReviewButton from './WriteReviewButton';
import { CardContainer, Container } from '../shared/styles';
import format from 'helpers/translations';

const Wrapper = styled(CardContainer)`
    display: flex;
    flex-direction: column;
    padding: ${Spacing16};
    background-color: ${ColorNeutralWhite};

    ${breakpoint.up(sizes.lg)} {
        padding: ${Spacing24};
    }
`;

const AverageRatingContainer = styled.div`
    display: flex;
    align-items: center;
`;

const AverageRating = styled.div`
    font-weight: bold;
    flex: 0 0 auto;
    line-height: 1.1;
    margin-right: 10px;
    color: ${ColorBrandBlue1};
    font-size: ${rem(46)};

    ${breakpoint.up(sizes.md)} {
        font-size: ${rem(60)};
    }
`;

const BasedOnContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const BasedOn = styled.div`
    font-weight: normal;
    opacity: 0.6;
`;

const Stars = styled(RatingStars)``;

const RatingSpreadtext = styled.div`
    margin-bottom: ${Spacing8};
`;

const WriteReviewButtonStyled = styled(WriteReviewButton)``;

const StyledCaption = styled(Typography.Caption)`
    cursor: pointer;

    margin-top: ${Spacing8};
    text-align: center;

    ${breakpoint.up(sizes.md)} {
        text-align: left;
    }
    b {
        text-decoration: underline;
    }
`;

const RatingSpreadContainer = styled(Container)`
    margin-bottom: ${Spacing16};
`;

const RatingSpreadComponent = styled(RatingSpread)``;

interface IRatingOverviewProps {
    model: ProductReviewListModel;
}

export const RatingOverview = (props: IRatingOverviewProps): JSX.Element => {
    const { toggle: openQuickview } = useQuickView('ReviewComplianceQuickview');
    const { model, ...rest } = props;
    const {
        reviewStatistics: { averageRating, reviewCount, recommendedPercent },
        complianceHeader
    } = model;
    const { t } = useTranslation();
    const basedOnText = format(t(BasedOnXReviews), reviewCount);
    const calculatedRecommendedPercentage = Math.round(recommendedPercent * 100);

    return (
        <Wrapper {...rest}>
            <Container>
                <AverageRatingContainer>
                    <AverageRating>{averageRating}</AverageRating>
                    <BasedOnContainer>
                        <BasedOn>{basedOnText}</BasedOn>
                        <Stars rating={averageRating} size="L" />
                    </BasedOnContainer>
                </AverageRatingContainer>
                {format(t(PercentRecommended), `${calculatedRecommendedPercentage}%`)}
            </Container>
            <RatingSpreadContainer>
                <RatingSpreadtext>{t(RatingSpreadHeadline)}</RatingSpreadtext>
                <RatingSpreadComponent model={model.reviewStatistics} />
            </RatingSpreadContainer>
            <WriteReviewButtonStyled model={model} />
            <StyledCaption size="md" onClick={() => openQuickview(true)}>
                {format(complianceHeader)}
            </StyledCaption>
        </Wrapper>
    );
};

export default RatingOverview;
